import type { PaymentTotals } from '../domain/models';
import type { HostDiscountSummary } from '../application/discountSummary';

/** El checkout web escucha este mensaje para ajustar el alto del iframe y mostrar el footer. */
export const PAYMENT_IFRAME_MESSAGE_SOURCE = 'carrefour-payment-iframe';
export const CHECKOUT_HOST_MESSAGE_SOURCE = 'carrefour-checkout-web';

/** Desglose y total a pagar tras `pre-initiate` (y cuota seleccionada en UI). */
export type PaymentHostPreviewPayload = {
  checkoutTotal: number;
  /** Totales del carrito simulado en VTEX (puede faltar si el back aún no expone `totals`). */
  totals?: PaymentTotals;
  /** Promos/cupones con nombre VTEX tras simular pago con tag de cuota. */
  discountSummary?: HostDiscountSummary;
  /**
   * Ahorro extra por promo de cuota (p. ej. 1 pago) cuando el desglose del host no incluye ese descuento.
   * Monto positivo en pesos; el host lo suma a la fila de descuentos.
   */
  installmentPromoDiscount?: number;
};

export type PaymentIframeToastKind = 'success' | 'error';

/** Notificación flotante en el host (checkout web); el iframe no muestra toast propio en embed. */
export function postPaymentHostToast(kind: PaymentIframeToastKind, message: string): void {
  if (!message.trim()) return;
  window.parent.postMessage(
    { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'toast', toastType: kind, message },
    '*',
  );
}

/** El checkout web redirige el top window a `/checkout/orderPlaced/` en la tienda VTEX. */
export function postPaymentHostApproved(orderId: string): void {
  const id = orderId.trim();
  if (!id) return;
  window.parent.postMessage(
    { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'payment-approved', orderId: id },
    '*',
  );
}

/** Tiempo que se espera la confirmación del host antes de navegar desde el propio iframe. */
const MERCADOPAGO_HOST_ACK_TIMEOUT_MS = 1500;

/**
 * Mercado Pago no se puede mostrar dentro de un iframe: se le pide al host (checkout web) que navegue la ventana
 * principal. El host valida el origen del mensaje y que la URL sea de Mercado Pago, y responde con un ack.
 * Si no hay host (el iframe es el documento principal) o no responde a tiempo (host sin esta versión), se llama
 * `onNoAnswer` para navegar desde el propio iframe.
 */
export function requestHostMercadoPagoRedirect(
  url: string,
  onNoAnswer: () => void,
  ackTimeoutMs: number = MERCADOPAGO_HOST_ACK_TIMEOUT_MS,
): void {
  if (window.parent === window) {
    onNoAnswer();
    return;
  }
  const finish = () => {
    window.clearTimeout(timer);
    window.removeEventListener('message', onMessage);
  };
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { source?: string; type?: string } | null;
    if (data?.source === CHECKOUT_HOST_MESSAGE_SOURCE && data.type === 'mercadopago-redirect-ack') finish();
  };
  const timer = window.setTimeout(() => {
    finish();
    onNoAnswer();
  }, ackTimeoutMs);
  window.addEventListener('message', onMessage);
  window.parent.postMessage({ source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'mercadopago-redirect', url }, '*');
}

/** Tasas de un plan con interés, en porcentaje (CFT, TEA y TNA). */
export interface PaymentHostRates {
  cft: number;
  tea: number;
  tna: number;
}

/**
 * El host suma estos intereses de cuotas al resumen del pedido (0 cuando no hay financiación) y muestra debajo las tasas
 * de cada tarjeta elegida (una o dos, en el orden de las tarjetas).
 */
export function postPaymentHostInterest(interest: number, rates: PaymentHostRates[] = []): void {
  if (window.parent === window) return;
  window.parent.postMessage(
    { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'payment-interest', interest, rates },
    '*',
  );
}

/** Totales del carrito simulados en `pre-initiate` + total según cuota elegida (checkout web, resumen lateral). */
export function postPaymentHostPreview(payload: PaymentHostPreviewPayload): void {
  if (window.parent === window) return;
  if (typeof payload.checkoutTotal !== 'number' || !Number.isFinite(payload.checkoutTotal)) return;
  window.parent.postMessage(
    { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'payment-preview', ...payload },
    '*',
  );
}

export function reportFrameHeight(): () => void {
  const root = document.getElementById('root');
  document.documentElement.style.minHeight = '0';
  document.documentElement.style.overflow = 'hidden';
  document.body.style.minHeight = '0';
  document.body.style.overflow = 'hidden';
  if (root) root.style.minHeight = '0';

  const post = () => {
    const height = Math.ceil(root?.scrollHeight ?? document.body.scrollHeight);
    if (height <= 0) return;
    window.parent.postMessage(
      { source: PAYMENT_IFRAME_MESSAGE_SOURCE, type: 'resize', height },
      '*',
    );
  };

  post();
  const observer = new ResizeObserver(post);
  if (root) observer.observe(root);
  window.addEventListener('resize', post);
  return () => {
    observer.disconnect();
    window.removeEventListener('resize', post);
  };
}
